// Admin v2 — Bevételi riportok (Figma ADM_09): szűrők (helyszín, riport típus, időszak),
// összesítők, napi bevétel grafikon, részletes táblázat; CSV és PDF (nyomtatás) export.
// Forrás: bookings (klub-foglalások ára) + match_history (mérkőzések a helyszín nevével).
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, query, Timestamp, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { fmtDateDot, fmtInt, pctDelta, toDate } from '../data';
import { Button, Card, Empty, Field, Input, PageHeader, SectionTitle, Select, Spinner, StatCard } from '../ui';

type Kind = 'daily' | 'weekly' | 'monthly';
interface Bk { clubId: string; club: string; at: Date; revenue: number }
interface Mt { venue: string; at: Date }

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function bucketKey(d: Date, k: Kind) {
  if (k === 'monthly') return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
  if (k === 'weekly') { const m = new Date(d); m.setDate(m.getDate() - ((m.getDay() + 6) % 7)); return `${fmtDateDot(m)} hét`; }
  return fmtDateDot(d);
}

export default function RevenuePage() {
  const now = new Date();
  const [club, setClub] = useState('all');
  const [kind, setKind] = useState<Kind>('daily');
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(iso(now));
  const [clubs, setClubs] = useState<{ id: string; name: string; prices: Record<string, number> }[]>([]);
  const [bks, setBks] = useState<Bk[] | null>(null);
  const [mts, setMts] = useState<Mt[]>([]);

  useEffect(() => {
    (async () => {
      const cs = await getDocs(collection(db, 'clubs')).catch(() => null);
      const list = await Promise.all((cs?.docs ?? []).map(async (d) => {
        const bs = await getDocs(collection(db, 'clubs', d.id, 'boards')).catch(() => null);
        const prices: Record<string, number> = {};
        bs?.docs.forEach((b) => { const v = b.data(); prices[String(v.order ?? 1)] = Number(v.priceBase ?? 4500) / Math.max(1, Number(v.priceMinutes ?? 60)); });
        return { id: d.id, name: String(d.data().name ?? 'Klub'), prices };
      }));
      setClubs(list.sort((a, b) => a.name.localeCompare(b.name, 'hu')));
      const since = Timestamp.fromDate(new Date(Date.now() - 400 * 864e5));
      const bsn = await getDocs(query(collection(db, 'bookings'), where('startAt', '>=', since), limit(10000))).catch(() => null);
      setBks((bsn?.docs ?? []).map((d) => {
        const v = d.data();
        if (v.state === 'cancelled') return null;
        const c = list.find((x) => x.id === v.clubId);
        const rev = typeof v.totalPrice === 'number' ? v.totalPrice : Math.round((c?.prices[String(v.board ?? 1)] ?? 0) * Number(v.durationMin ?? 60));
        return { clubId: String(v.clubId ?? ''), club: c?.name ?? String(v.clubName ?? ''), at: toDate(v.startAt) ?? new Date(0), revenue: rev };
      }).filter((x): x is Bk => Boolean(x)));
      const msn = await getDocs(query(collection(db, 'match_history'), where('date', '>=', since), limit(10000))).catch(() => null);
      setMts((msn?.docs ?? []).map((d) => ({ venue: String(d.data().venue ?? ''), at: toDate(d.data().date) ?? new Date(0) })));
    })();
  }, []);

  const r = useMemo(() => {
    if (!bks) return null;
    const a = new Date(`${from}T00:00:00`); const z = new Date(`${to}T23:59:59`);
    const clubName = clubs.find((c) => c.id === club)?.name;
    const inR = (d: Date) => d >= a && d <= z;
    const b = bks.filter((x) => inR(x.at) && x.at <= new Date() && (club === 'all' || x.clubId === club));
    const m = mts.filter((x) => inR(x.at) && (club === 'all' || x.venue === clubName));
    const len = z.getTime() - a.getTime();
    const prevB = bks.filter((x) => x.at >= new Date(a.getTime() - len) && x.at < a && (club === 'all' || x.clubId === club));
    const total = b.reduce((s, x) => s + x.revenue, 0);
    const prevTotal = prevB.reduce((s, x) => s + x.revenue, 0);
    const days = Math.max(1, Math.round(len / 864e5));
    const daily: { label: string; v: number }[] = [];
    for (let i = 0; i < days; i++) {
      const d0 = new Date(a.getTime() + i * 864e5); const d1 = new Date(d0.getTime() + 864e5);
      daily.push({ label: fmtDateDot(d0), v: b.filter((x) => x.at >= d0 && x.at < d1).reduce((s, x) => s + x.revenue, 0) });
    }
    const groups = new Map<string, { key: string; club: string; rev: number; matches: number; sort: number }>();
    b.forEach((x) => {
      const k = `${bucketKey(x.at, kind)}|${x.club}`;
      const g = groups.get(k) ?? { key: bucketKey(x.at, kind), club: x.club, rev: 0, matches: 0, sort: x.at.getTime() };
      g.rev += x.revenue; g.sort = Math.max(g.sort, x.at.getTime()); groups.set(k, g);
    });
    m.forEach((x) => { const k = `${bucketKey(x.at, kind)}|${x.venue}`; const g = groups.get(k); if (g) g.matches += 1; });
    const table = Array.from(groups.values()).sort((p, q) => q.sort - p.sort);
    return { total, prevTotal, matches: m.length, avgDay: Math.round(total / days), daily, table };
  }, [bks, mts, club, kind, from, to, clubs]);

  const exportCsv = () => {
    if (!r) return;
    const rows = [['Dátum', 'Helyszín', 'Bevétel (Ft)', 'Mérkőzések', 'Átl. bevétel (Ft)'], ...r.table.map((t) => [t.key, t.club, String(t.rev), String(t.matches), t.matches ? String(Math.round(t.rev / t.matches)) : ''])];
    const csv = '﻿' + rows.map((x) => x.map((c) => `"${c.replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const el = document.createElement('a');
    el.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    el.download = `dartiqon-bevetel-${from}_${to}.csv`;
    el.click();
    URL.revokeObjectURL(el.href);
  };

  if (!r) return <Spinner />;
  const W = 640, H = 170;
  const max = Math.max(1, ...r.daily.map((p) => p.v));
  const step = r.daily.length > 1 ? W / (r.daily.length - 1) : W;
  const line = r.daily.map((p, i) => `${i ? 'L' : 'M'}${(i * step).toFixed(1)},${(H - 6 - (p.v / max) * (H - 20)).toFixed(1)}`).join(' ');

  return (
    <div className="print:bg-white">
      <PageHeader title="Bevételi riportok" sub="Részletes bevételi jelentések generálása és exportálása" right={<>
        <Button kind="ghost" icon="DownloadSimple" onClick={exportCsv}>CSV exportálás</Button>
        <Button icon="FileText" onClick={() => window.print()}>PDF exportálás</Button>
      </>} />

      <Card className="!p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <Field label="Helyszín"><Select value={club} onChange={setClub} options={[{ value: 'all', label: 'Összes helyszín' }, ...clubs.map((c) => ({ value: c.id, label: c.name }))]} /></Field>
          <Field label="Riport típus"><Select<Kind> value={kind} onChange={setKind} options={[{ value: 'daily', label: 'Napi' }, { value: 'weekly', label: 'Heti' }, { value: 'monthly', label: 'Havi' }]} /></Field>
          <Field label="Kezdő dátum"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="[color-scheme:dark]" /></Field>
          <Field label="Befejező dátum"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="[color-scheme:dark]" /></Field>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <StatCard icon="Coins" tone="green" label="Összes bevétel" value={`${fmtInt(r.total)} Ft`} valueColor="#2bc48a" delta={r.prevTotal ? pctDelta(r.total, r.prevTotal) : null} />
        <StatCard icon="Target" tone="cyan" label="Összes mérkőzés" value={fmtInt(r.matches)} />
        <StatCard icon="TrendUp" tone="gold" label="Átl. bevétel / nap" value={`${fmtInt(r.avgDay)} Ft`} valueColor="#d9a514" />
      </div>

      <Card className="!p-5 mt-6">
        <SectionTitle>Bevétel naponta</SectionTitle>
        {r.total === 0 ? <Empty>Nincs bevétel a kiválasztott időszakban.</Empty> : (
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[170px]" preserveAspectRatio="none">
            <defs><linearGradient id="dqRep" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2bc48a" stopOpacity="0.35" /><stop offset="100%" stopColor="#2bc48a" stopOpacity="0" /></linearGradient></defs>
            {[0, 1, 2, 3].map((i) => <line key={i} x1="0" x2={W} y1={(H / 3) * i} y2={(H / 3) * i} stroke="#31383D" strokeOpacity="0.6" vectorEffect="non-scaling-stroke" />)}
            <path d={`${line} L${W},${H} L0,${H} Z`} fill="url(#dqRep)" />
            <path d={line} fill="none" stroke="#2bc48a" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
          </svg>
        )}
      </Card>

      <div className="mt-6 bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[720px] text-left">
          <thead>
            <tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
              <th className="pl-5 pr-4 h-[39px] font-semibold">Dátum</th><th className="px-4 font-semibold">Helyszín</th><th className="px-4 font-semibold">Bevétel</th>
              <th className="px-4 font-semibold">Mérkőzések</th><th className="px-4 font-semibold">Átl. bevétel</th>
            </tr>
          </thead>
          <tbody>
            {r.table.slice(0, 100).map((t) => (
              <tr key={`${t.key}|${t.club}`} className="border-t border-[#31383D] h-[46px]">
                <td className="pl-5 pr-4 text-[13px] font-semibold text-white">{t.key}</td>
                <td className="px-4 text-[13px] text-[#B8B8C3]">{t.club || '—'}</td>
                <td className="px-4 text-[13px] text-[#B8B8C3]">{fmtInt(t.rev)} Ft</td>
                <td className="px-4 text-[13px] text-[#B8B8C3]">{t.matches}</td>
                <td className="px-4 text-[13px] text-[#B8B8C3]">{t.matches ? `${fmtInt(t.rev / t.matches)} Ft` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {r.table.length === 0 && <Empty>Nincs adat a kiválasztott szűrőkkel.</Empty>}
      </div>
    </div>
  );
}
